import { z } from "zod";
import {
  Message,
  MessageSchema,
  UnifiedMessage,
  MessageFilter,
  MessageFilterSchema,
  MessageSort,
  MessageStats,
  MessageListResponse,
  MessageListResponseSchema,
} from "./message";
import { Inbox, InboxSchema } from "./inbox";

// ============================================================================
// STATE SCHEMAS
// ============================================================================

export const MessagesStateSchema = z.object({
  messagesByInbox: z.record(z.string(), z.record(z.string(), z.array(MessageSchema))),
  loadingStates: z.record(z.string(), z.record(z.string(), z.boolean())),
  errorStates: z.record(z.string(), z.record(z.string(), z.string().nullable())),
  paginationInfo: z.record(
    z.string(),
    z.record(
      z.string(),
      z.object({
        hasMore: z.boolean(),
        nextPageToken: z.string().optional(),
      })
    )
  ),
  unreadCounts: z.record(z.string(), z.record(z.string(), z.number().int().nonnegative())),
}).strict();

export type MessagesState = z.infer<typeof MessagesStateSchema>;

// ============================================================================
// UTILITY CLASSES
// ============================================================================

export class UnifiedMessageUtils {
  static formatMessageForDisplay(message: UnifiedMessage) {
    return {
      ...message,
      formattedDate: new Date(message.receivedDateTime).toLocaleString(),
      shortSender: message.sender.name.split(" ")[0],
      hasMultipleRecipients: message.recipients.length > 1,
    };
  }

  static getProviderInfo(message: UnifiedMessage) {
    return {
      provider: message.provider,
      displayName: message.provider.replace("-", " ").replace(/\b\w/g, (l) => l.toUpperCase()),
      color: this.getProviderColor(message.provider),
    };
  }

  static getThreadInfo(message: UnifiedMessage) {
    return {
      threadId: message.threadId,
      hasReplies: message.hasReplies,
      replyCount: message.replyCount || 0,
    };
  }

  static filterMessages(
    messages: UnifiedMessage[],
    filter: MessageFilter
  ): UnifiedMessage[] {
    return messages.filter((message) => {
      if (filter.isRead !== undefined && message.isRead !== filter.isRead) return false;
      if (filter.hasAttachments !== undefined && message.hasAttachments !== filter.hasAttachments)
        return false;
      if (filter.importance && message.importance !== filter.importance) return false;
      if (filter.providers && !filter.providers.includes(message.provider)) return false;
      if (filter.inboxIds && !filter.inboxIds.includes(message.inboxId)) return false;
      if (filter.search) {
        const searchTerm = filter.search.toLowerCase();
        const searchableText =
          `${message.subject} ${message.sender.name} ${message.bodyPreview}`.toLowerCase();
        if (!searchableText.includes(searchTerm)) return false;
      }
      return true;
    });
  }

  static sortMessages(messages: UnifiedMessage[], sort: MessageSort): UnifiedMessage[] {
    return [...messages].sort((a, b) => {
      let comparison = 0;

      switch (sort.field) {
        case "receivedDateTime":
          comparison =
            new Date(a.receivedDateTime).getTime() - new Date(b.receivedDateTime).getTime();
          break;
        case "sentDateTime":
          comparison = new Date(a.sentDateTime).getTime() - new Date(b.sentDateTime).getTime();
          break;
        case "sender":
          comparison = a.sender.name.localeCompare(b.sender.name);
          break;
        case "subject":
          comparison = a.subject.localeCompare(b.subject);
          break;
        case "importance":
          const importanceOrder = { high: 3, normal: 2, low: 1 };
          comparison = importanceOrder[a.importance] - importanceOrder[b.importance];
          break;
        case "provider":
          comparison = a.provider.localeCompare(b.provider);
          break;
        default:
          comparison =
            new Date(a.receivedDateTime).getTime() - new Date(b.receivedDateTime).getTime();
      }

      return sort.order === "desc" ? -comparison : comparison;
    });
  }

  static calculateStats(messages: UnifiedMessage[]): MessageStats {
    const total = messages.length;
    const unread = messages.filter((m) => !m.isRead).length;
    const important = messages.filter((m) => m.importance === "high").length;
    const withAttachments = messages.filter((m) => m.hasAttachments).length;

    const byProvider: Record<string, { total: number; unread: number; important: number }> = {};
    const byInbox: Record<string, { total: number; unread: number; important: number }> = {};

    messages.forEach((m) => {
      // Provider stats
      if (!byProvider[m.provider]) {
        byProvider[m.provider] = { total: 0, unread: 0, important: 0 };
      }
      byProvider[m.provider].total++;
      if (!m.isRead) byProvider[m.provider].unread++;
      if (m.importance === "high") byProvider[m.provider].important++;

      // Inbox stats
      if (!byInbox[m.inboxId]) {
        byInbox[m.inboxId] = { total: 0, unread: 0, important: 0 };
      }
      byInbox[m.inboxId].total++;
      if (!m.isRead) byInbox[m.inboxId].unread++;
      if (m.importance === "high") byInbox[m.inboxId].important++;
    });

    return {
      total,
      unread,
      important,
      withAttachments,
      byProvider,
      byInbox,
    };
  }

  private static getProviderColor(provider: string): string {
    const colors: Record<string, string> = {
      "google-mail": "text-red-500",
      "outlook-oauth": "text-blue-500",
      "slack-oauth": "text-purple-500",
    };
    return colors[provider] || "text-gray-500";
  }
}

// ============================================================================
// VALIDATION HELPERS
// ============================================================================

/**
 * Validate a message against the Message schema
 * Throws ZodError if validation fails
 */
export function validateMessage(data: unknown): Message {
  return MessageSchema.parse(data);
}

/**
 * Safely validate a message, returning null if validation fails
 */
export function validateMessageSafe(data: unknown): Message | null {
  const result = MessageSchema.safeParse(data);
  return result.success ? result.data : null;
}

/**
 * Validate a message filter
 */
export function validateMessageFilter(data: unknown): MessageFilter {
  return MessageFilterSchema.parse(data);
}

/**
 * Validate a message list response
 */
export function validateMessageListResponse(data: unknown): MessageListResponse {
  return MessageListResponseSchema.parse(data);
}

/**
 * Validate an inbox
 */
export function validateInbox(data: unknown): Inbox {
  return InboxSchema.parse(data);
}

/**
 * Validate an array of inboxes
 */
export function validateInboxes(data: unknown): Inbox[] {
  return z.array(InboxSchema).parse(data);
}
